/**
 * WorkspaceSection Component
 *
 * Workspace settings section with auto-saving name and description.
 * Features danger zone for workspace deletion (owner only).
 *
 * @module pages/SettingsPage/components/WorkspaceSection
 */

import {
  type ChangeEvent,
  type ReactElement,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Stack, TextInput, Textarea } from '@mantine/core';

import { useWorkspace } from '@/hooks/useWorkspace';
import { NotificationService } from '@/services/notificationService';
import { SettingsToggleRow } from '@/components/common/SettingsToggleRow';
import { TEXTAREA_MAX_ROWS, TEXTAREA_MIN_ROWS } from '@/constants/ui';
import { DEFAULT_SECTION, ROUTES } from '@/routes/routes';

import { AutoSaveIndicator } from './AutoSaveIndicator';
import { DeleteWorkspaceSection } from './DeleteWorkspaceSection';
import { MapConfigSummary } from './parts/MapConfigSummary';
import styles from './WorkspaceSection.module.css';

/** Debounce delay for auto-saving workspace fields (milliseconds) */
const WORKSPACE_DEBOUNCE_MS = 800;

/** Duration to show "saved" status before returning to idle (milliseconds) */
const SAVED_STATUS_DISPLAY_MS = 2000;

type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const WorkspaceSection = memo(function WorkspaceSection(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentWorkspace, updateWorkspace, deleteWorkspace } = useWorkspace();

  const workspaceName = currentWorkspace?.name ?? '';
  const workspaceDescription = currentWorkspace?.description ?? '';
  const workspaceId = currentWorkspace?.workspaceId ?? '';
  const workspaceShortId = currentWorkspace?.shortId ?? '';
  const isOwner = currentWorkspace?.isOwner ?? false;
  const autoStartTasksEnabled =
    currentWorkspace?.autoStartTasksEnabled ?? false;

  const [localName, setLocalName] = useState(workspaceName);
  const [localDescription, setLocalDescription] =
    useState(workspaceDescription);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const localNameRef = useRef(localName);
  const localDescriptionRef = useRef(localDescription);

  useEffect(() => {
    setLocalName(workspaceName);
    setLocalDescription(workspaceDescription);
    localNameRef.current = workspaceName;
    localDescriptionRef.current = workspaceDescription;
  }, [workspaceName, workspaceDescription]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  const latestRef = useRef({
    workspaceName,
    workspaceDescription,
    workspaceId,
  });
  useEffect(() => {
    latestRef.current = { workspaceName, workspaceDescription, workspaceId };
  });

  const saveWorkspace = useCallback(async () => {
    const {
      workspaceName: storeName,
      workspaceDescription: storeDescription,
      workspaceId: storeId,
    } = latestRef.current;
    const name = localNameRef.current;
    const description = localDescriptionRef.current;
    const trimmedName = name.trim();
    if (!trimmedName || !storeId) return;

    const nameChanged = trimmedName !== storeName;
    const descriptionChanged = description.trim() !== storeDescription;
    if (!nameChanged && !descriptionChanged) return;

    setAutoSaveStatus('saving');
    const result = await updateWorkspace(storeId, {
      name: trimmedName,
      description: description.trim(),
    });

    if (result.success) {
      setAutoSaveStatus('saved');
      clearTimeout(savedTimeoutRef.current);
      savedTimeoutRef.current = setTimeout(
        () => setAutoSaveStatus('idle'),
        SAVED_STATUS_DISPLAY_MS
      );
    } else {
      console.error('Failed to update workspace:', result.error);
      setAutoSaveStatus('error');
      NotificationService.showError(t('settings.workspace.updateError'));
    }
  }, [updateWorkspace, t]);

  const scheduleSave = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => void saveWorkspace(),
      WORKSPACE_DEBOUNCE_MS
    );
  }, [saveWorkspace]);

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      setLocalName(value);
      localNameRef.current = value;
      scheduleSave();
    },
    [scheduleSave]
  );

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.currentTarget.value;
      setLocalDescription(value);
      localDescriptionRef.current = value;
      scheduleSave();
    },
    [scheduleSave]
  );

  const handleAutoStartToggle = useCallback(async () => {
    if (!workspaceId) return;
    setAutoSaveStatus('saving');
    const result = await updateWorkspace(workspaceId, {
      autoStartTasksEnabled: !autoStartTasksEnabled,
    });
    if (result.success) {
      setAutoSaveStatus('saved');
      clearTimeout(savedTimeoutRef.current);
      savedTimeoutRef.current = setTimeout(
        () => setAutoSaveStatus('idle'),
        SAVED_STATUS_DISPLAY_MS
      );
    } else {
      setAutoSaveStatus('error');
      NotificationService.showError(t('settings.workspace.updateError'));
    }
  }, [workspaceId, autoStartTasksEnabled, updateWorkspace, t]);

  // Deleting the current workspace must move the user off its (now stale) URL:
  // switch to the first remaining workspace's dashboard, or fall back to the
  // onboarding screen when none are left. `replace` keeps the deleted URL out
  // of history so the back button can't return to a non-existent workspace.
  const handleDeleteWorkspace = useCallback(
    async (id: string) => {
      const result = await deleteWorkspace(id);
      if (result.success) {
        if (result.nextWorkspaceShortId) {
          navigate(`/w/${result.nextWorkspaceShortId}/${DEFAULT_SECTION}`, {
            replace: true,
          });
        } else {
          navigate(ROUTES.ONBOARDING, { replace: true });
        }
      }
      return result;
    },
    [deleteWorkspace, navigate]
  );

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.title}>{t('settings.workspace.sectionTitle')}</h2>
        {isOwner && <AutoSaveIndicator status={autoSaveStatus} />}
      </div>

      <hr className={styles.divider} />

      {!isOwner ? (
        <p className={styles.notOwnerText}>
          {t('settings.workspace.notOwner')}
        </p>
      ) : (
        <>
          <Stack gap="lg">
            <TextInput
              label={t('settings.workspace.name')}
              placeholder={t('settings.workspace.namePlaceholder')}
              value={localName}
              onChange={handleNameChange}
            />

            <Textarea
              label={t('settings.workspace.description')}
              placeholder={t('settings.workspace.descriptionPlaceholder')}
              value={localDescription}
              onChange={handleDescriptionChange}
              autosize
              minRows={TEXTAREA_MIN_ROWS}
              maxRows={TEXTAREA_MAX_ROWS}
            />

            <SettingsToggleRow
              label={t('settings.workspace.autoStartTasks')}
              description={t('settings.workspace.autoStartTasksDescription')}
              checked={autoStartTasksEnabled}
              onChange={handleAutoStartToggle}
              disabled={autoSaveStatus === 'saving'}
            />
          </Stack>

          <MapConfigSummary workspaceShortId={workspaceShortId} />

          <hr className={styles.deleteWorkspaceDivider} />

          <DeleteWorkspaceSection
            workspaceName={workspaceName}
            workspaceId={workspaceId}
            onDelete={handleDeleteWorkspace}
          />
        </>
      )}
    </div>
  );
});
